// Logic for the focus dashboard - see doc/focus-dashboard-concept.org.
//
// Pure throughout, so the session arithmetic can be tested without a clock or a
// rendered component: every function that needs "now" takes it as an argument.

export const FOCUS_DURATIONS_MINUTES = [10, 20, 25, 45];
export const DEFAULT_FOCUS_MINUTES = 20;
export const FOCUS_STRIP_DAYS = 14;
// Bounded so a persisted setting cannot grow without limit and bloat the config file.
export const MAX_LOGGED_SESSIONS = 500;

export const OUTCOME_COMPLETED = 'completed';
export const OUTCOME_EARLY = 'early';
export const OUTCOME_ABANDONED = 'abandoned';

// Rewards the user has not replaced yet. Prompts, never payouts - the app cannot hand
// over a coffee, and pretending it hands over currency is what the concept forbids.
export const DEFAULT_REWARDS = [
  'Take five.',
  'Make a coffee.',
  'Stand up and stretch.',
  'Step outside for a minute.',
  'Put on a favourite track.',
];

const startOfLocalDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const isSameLocalDay = (a, b) =>
  !!a &&
  !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * Seconds left in a running session.
 *
 * INFO: Derived from `startedAt` against the clock, never by decrementing a counter.
 * setInterval is throttled hard in background tabs, so a tick-counting timer silently
 * stretches a 20-minute session; this also makes a session survive a reload.
 *
 * `pausedMillis` is the total time already spent paused, and `pausedAt` is set while
 * paused so that the pause itself does not consume the session.
 */
export const secondsRemaining = (session, now) => {
  if (!session) return 0;

  const elapsed =
    (session.pausedAt ? session.pausedAt : now) -
    session.startedAt -
    (session.pausedMillis || 0);

  return Math.max(0, Math.ceil((session.seconds * 1000 - elapsed) / 1000));
};

export const isSessionElapsed = (session, now) => !!session && secondsRemaining(session, now) === 0;

/** Seconds actually spent focusing, for the log. */
export const secondsSpent = (session, now) => {
  if (!session) return 0;

  const elapsed =
    (session.pausedAt ? session.pausedAt : now) -
    session.startedAt -
    (session.pausedMillis || 0);

  return Math.max(0, Math.min(session.seconds, Math.round(elapsed / 1000)));
};

/** mm:ss, and never a negative sign. */
export const formatClock = (totalSeconds) => {
  const safe = Math.max(0, Math.floor(totalSeconds || 0));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;

  return `${minutes}:${`${seconds}`.padStart(2, '0')}`;
};

export const startSession = ({ task, minutes, now }) => ({
  startedAt: now,
  seconds: Math.max(1, Math.round(minutes * 60)),
  taskText: task ? task.text : '',
  path: task ? task.path : null,
  pausedMillis: 0,
  pausedAt: null,
});

export const pauseSession = (session, now) =>
  !session || session.pausedAt ? session : { ...session, pausedAt: now };

export const resumeSession = (session, now) =>
  !session || !session.pausedAt
    ? session
    : {
        ...session,
        pausedAt: null,
        pausedMillis: (session.pausedMillis || 0) + (now - session.pausedAt),
      };

/**
 * The log entry a finished session produces.
 *
 * A session ended by hand before the clock ran out is 'early', not a failure: the task
 * is the goal, not the duration. Giving up is recorded plainly, because a tool that
 * hides quitting teaches you to lie to it.
 */
export const sessionToLogEntry = (session, { now, outcome }) => ({
  startedAt: session.startedAt,
  seconds: secondsSpent(session, now),
  taskText: session.taskText,
  path: session.path,
  outcome,
});

export const appendSession = (log, entry) =>
  [...(log || []), entry].slice(-MAX_LOGGED_SESSIONS);

/** Sessions that counted as focus - abandoned ones do not. */
const isCounted = (entry) => !!entry && entry.outcome !== OUTCOME_ABANDONED;

export const sessionsOn = (log, date) =>
  (log || []).filter((entry) => isCounted(entry) && isSameLocalDay(new Date(entry.startedAt), date));

export const secondsOn = (log, date) =>
  sessionsOn(log, date).reduce((total, entry) => total + (entry.seconds || 0), 0);

/**
 * One column per day for the strip, oldest first.
 */
export const dayStrip = (log, today, count = FOCUS_STRIP_DAYS) => {
  const end = startOfLocalDay(today);
  const columns = [];

  for (let offset = count - 1; offset >= 0; offset -= 1) {
    const date = new Date(end.getFullYear(), end.getMonth(), end.getDate() - offset);
    const sessions = sessionsOn(log, date);

    columns.push({
      date,
      sessions: sessions.length,
      seconds: sessions.reduce((total, entry) => total + (entry.seconds || 0), 0),
    });
  }

  return columns;
};

/**
 * Consecutive days ending today that had at least one counted session.
 *
 * Today not having one yet does not end the run - it has not failed, it just has not
 * happened. Anything else would greet you with a broken streak every morning.
 */
export const currentRun = (log, today) => {
  let run = 0;
  const start = startOfLocalDay(today);

  for (let offset = 0; offset < MAX_LOGGED_SESSIONS; offset += 1) {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() - offset);
    const count = sessionsOn(log, date).length;

    if (count > 0) {
      run += 1;
    } else if (offset > 0) {
      break;
    }
  }

  return run;
};

/** The longest run ever recorded, for competition with a past self. */
export const bestRun = (log) => {
  const days = [
    ...new Set(
      (log || [])
        .filter(isCounted)
        .map((entry) => startOfLocalDay(new Date(entry.startedAt)).getTime())
    ),
  ].sort((a, b) => a - b);

  let best = 0;
  let run = 0;
  let previous = null;

  days.forEach((time) => {
    const date = new Date(time);
    const expected = previous
      ? new Date(previous.getFullYear(), previous.getMonth(), previous.getDate() + 1)
      : null;

    run = expected && isSameLocalDay(date, expected) ? run + 1 : 1;
    best = Math.max(best, run);
    previous = date;
  });

  return best;
};

/**
 * A reward to offer, varied by session count rather than at random.
 *
 * INFO: Deliberately not Math.random(). Randomised reward is the variable-ratio
 * schedule the concept rejects on ethical grounds; rotating deterministically gives
 * variety without building a slot machine.
 */
export const rewardFor = (rewards, sessionCount) => {
  const list = (rewards || []).filter((reward) => `${reward || ''}`.trim().length > 0);
  if (!list.length) return null;

  return list[Math.abs(sessionCount || 0) % list.length];
};

/** Normalise whatever the persisted setting holds into the shape the page expects. */
export const normalizeFocusLog = (stored) => {
  const raw = stored && typeof stored.toJS === 'function' ? stored.toJS() : stored || {};
  const sessions = Array.isArray(raw.sessions) ? raw.sessions : [];

  return {
    sessions: sessions
      .filter((entry) => entry && typeof entry.startedAt === 'number')
      .slice(-MAX_LOGGED_SESSIONS),
    rewards: Array.isArray(raw.rewards) && raw.rewards.length ? raw.rewards : DEFAULT_REWARDS,
  };
};
